/**
 * zod v4 request/response schemas shared by server validation and (later)
 * react-hook-form resolvers. Runtime-neutral: no process.env / import.meta reads.
 * Grows in PRP-4+.
 */

import { z } from 'zod';

export const loginRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(12).max(256),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordSchema>;

export const adminMeSchema = z.object({ id: z.string(), username: z.string() });

// ---- PRP-4: contact (+ PRP-12 stateless forms) ----
/** Honeypot: a visually hidden field humans never fill. Present-but-non-empty = bot. */
const honeypot = z.string().max(0, 'leave empty').optional();

export const contactRequestSchema = z.object({
  name: z.string().min(2).max(200),
  email: z.string().trim().email(),
  phone: z.string().max(50).optional(),
  organization: z.string().max(200).optional(),
  /** @deprecated pre-PRP-12 field name — still accepted, mapped to organization server-side. */
  company: z.string().max(200).optional(),
  subject: z.string().max(300).optional(),
  message: z.string().min(10).max(5000),
  website: honeypot,
});
export type ContactRequest = z.infer<typeof contactRequestSchema>;

// ---- PRP-12: private assistance request (FreshStart Connects, PRD 9.8) ----
export const assistanceRequestSchema = z.object({
  name: z.string().min(2, 'Please tell us your name').max(200),
  email: z.string().trim().email('Please enter a valid email'),
  phone: z.string().max(50).optional(),
  preferredContact: z.enum(['email', 'phone', 'text']),
  message: z
    .string()
    .min(10, 'Please share a little more (at least 10 characters)')
    .max(4000, 'Please keep it under 4,000 characters'),
  newsletterOptIn: z.boolean().default(false),
  website: honeypot,
});
export type AssistanceRequest = z.infer<typeof assistanceRequestSchema>;
export type AssistanceRequestInput = z.input<typeof assistanceRequestSchema>;

// ---- PRP-12: newsletter signup → client-owned Google Sheet (PRD 9.2) ----
export const newsletterSignupSchema = z.object({
  name: z.string().min(2, 'Please tell us your name').max(200),
  email: z.string().trim().email('Please enter a valid email'),
  phone: z.string().max(50).optional(),
  sourcePage: z.string().max(200),
  consent: z.literal(true, { message: 'Please confirm you want to hear from us' }),
  website: honeypot,
});
export type NewsletterSignup = z.infer<typeof newsletterSignupSchema>;

// ---- PRP-4: customer magic-link auth ----
export const magicLinkRequestSchema = z.object({ email: z.string().email() });
export type MagicLinkRequest = z.infer<typeof magicLinkRequestSchema>;

export const verifyTokenSchema = z.object({ token: z.string().min(20) });
export type VerifyTokenRequest = z.infer<typeof verifyTokenSchema>;

// ---- PRP-4: invoices & payments ----
export const lineItemSchema = z.object({
  description: z.string().min(1),
  quantity: z.number().int().positive(),
  unitCents: z.number().int().nonnegative(),
});
export type LineItem = z.infer<typeof lineItemSchema>;

// SANITIZED public shape — no client PII, no internal ids, no PI id, no token echo
export const invoicePublicSchema = z.object({
  status: z.enum(['open', 'paid', 'void']),
  amountCents: z.number().int(),
  currency: z.string(),
  lineItems: z.array(lineItemSchema),
  dueDate: z.string().nullable(),
});
export type InvoicePublic = z.infer<typeof invoicePublicSchema>;

export const payResponseSchema = z.object({
  clientSecret: z.string(),
  publishableKey: z.string(),
});
export type PayResponse = z.infer<typeof payResponseSchema>;

export const clientUpsertSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  notes: z.string().optional(),
});
export type ClientUpsert = z.infer<typeof clientUpsertSchema>;

export const productUpsertSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(['product', 'service', 'deposit']),
  priceCents: z.number().int().nonnegative(),
  currency: z.string().default('usd'),
  active: z.boolean().default(true),
  bookable: z.boolean().default(false),
  durationMinutes: z.number().int().positive().nullable().optional(),
  paymentMode: z.enum(['none', 'deposit', 'full']).default('none'),
  depositCents: z.number().int().positive().nullable().optional(),
  imageUrl: z
    .string()
    .max(500)
    .regex(/^(https?:\/\/|\/)\S+$/, 'must be an absolute URL or site-relative /path')
    .nullable()
    .optional(),
  category: z.string().max(100).nullable().optional(),
});
export type ProductUpsert = z.infer<typeof productUpsertSchema>;

// ---- PRP-9: public storefront ----
export const productPublicSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  priceCents: z.number().int(),
  currency: z.string(),
  imageUrl: z.string().nullable(),
  category: z.string().nullable(),
});
export type ProductPublic = z.infer<typeof productPublicSchema>;

export const checkoutRequestSchema = z.object({
  name: z.string().min(1).max(120),
  email: z.string().email(),
  items: z
    .array(z.object({ productId: z.string().uuid(), quantity: z.number().int().min(1).max(20) }))
    .min(1)
    .max(20),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

export const checkoutResponseSchema = z.object({ token: z.string() });
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

export const invoiceCreateSchema = z.object({
  clientId: z.string().uuid().nullable().optional(),
  lineItems: z.array(lineItemSchema).min(1),
  dueDate: z.string().datetime().nullable().optional(),
  currency: z.string().default('usd'),
  sendEmail: z.boolean().default(false), // email invoiceLink on create
}); // amountCents derived server-side = sum(quantity * unitCents)
export type InvoiceCreate = z.infer<typeof invoiceCreateSchema>;

export const refundRequestSchema = z.object({
  amountCents: z.number().int().positive().optional(), // omitted = full remaining refund
  reason: z.string().max(500).optional(),
});
export type RefundRequest = z.infer<typeof refundRequestSchema>;

// ---- PRP-4: admin email center ----
export const emailTemplateUpsertSchema = z.object({
  subject: z.string().min(1),
  body: z.string().min(1),
});
export type EmailTemplateUpsert = z.infer<typeof emailTemplateUpsertSchema>;

export const automationUpdateSchema = z.object({
  delayMinutes: z.number().int().nonnegative().optional(),
  enabled: z.boolean().optional(),
});
export type AutomationUpdate = z.infer<typeof automationUpdateSchema>;

// ---- PRP-5: booking ----
function diffDays(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

export const availabilityQuerySchema = z
  .object({
    productId: z.string().uuid(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // 'YYYY-MM-DD'
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  })
  .refine((q) => q.to >= q.from, { message: 'to must be on/after from' })
  .refine((q) => diffDays(q.from, q.to) <= 31, { message: 'range max 31 days' });

export const createBookingSchema = z.object({
  productId: z.string().uuid(),
  startsAt: z.string().datetime(), // UTC instant of the chosen slot
  name: z.string().min(1).max(120),
  email: z.string().email(),
  phone: z.string().max(40).optional(),
  notes: z.string().max(1000).optional(),
});
export type CreateBookingInput = z.infer<typeof createBookingSchema>;

export const manageActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('cancel') }),
  z.object({ action: z.literal('reschedule'), startsAt: z.string().datetime() }),
]);
export type ManageAction = z.infer<typeof manageActionSchema>;

export const slotSchema = z.object({ startsAt: z.string().datetime(), endsAt: z.string().datetime() });
export type Slot = z.infer<typeof slotSchema>;

export const publicBookingSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(['pending', 'confirmed', 'cancelled', 'completed', 'no_show']),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  product: z.object({
    id: z.string().uuid(),
    name: z.string(),
    durationMinutes: z.number(),
    paymentMode: z.enum(['none', 'deposit', 'full']),
    priceCents: z.number(),
    depositCents: z.number().nullable(),
  }),
  notes: z.string().nullable(),
});
export type PublicBooking = z.infer<typeof publicBookingSchema>;

export const createBookingResponseSchema = z.object({
  booking: publicBookingSchema,
  manageToken: z.string(),
  clientSecret: z.string().nullable(), // non-null => payment step required
  publishableKey: z.string().nullable(),
});
export type CreateBookingResponse = z.infer<typeof createBookingResponseSchema>;

export const adminBookingListQuerySchema = z.object({
  status: z.enum(['pending', 'confirmed', 'cancelled', 'completed', 'no_show']).optional(),
  clientId: z.string().uuid().optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(25),
});

export const adminBookingPatchSchema = z.object({
  status: z.enum(['confirmed', 'cancelled', 'completed', 'no_show']),
});

export const availabilityRuleSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6),
    startTime: z.string().regex(/^\d{2}:\d{2}$/),
    endTime: z.string().regex(/^\d{2}:\d{2}$/),
    active: z.boolean().default(true),
  })
  .refine((r) => r.endTime > r.startTime, { message: 'endTime after startTime' });

export const availabilityExceptionSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    startTime: z.string().regex(/^\d{2}:\d{2}$/).nullable().default(null),
    endTime: z.string().regex(/^\d{2}:\d{2}$/).nullable().default(null),
    reason: z.string().max(200).optional(),
  })
  .refine((e) => (e.startTime === null) === (e.endTime === null), {
    message: 'startTime/endTime must both be set or both null',
  });

// PRP-8: recurring availability blocks (dayOfWeek null = every day)
export const availabilityBlockSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6).nullable().default(null),
    startTime: z.string().regex(/^\d{2}:\d{2}$/),
    endTime: z.string().regex(/^\d{2}:\d{2}$/),
    label: z.string().max(60).optional(),
    active: z.boolean().default(true),
  })
  .refine((b) => b.endTime > b.startTime, { message: 'endTime after startTime' });

export const availabilityBlockPatchSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6).nullable().optional(),
    startTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    endTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    label: z.string().max(60).nullable().optional(),
    active: z.boolean().optional(),
  })
  .refine((b) => !(b.startTime && b.endTime) || b.endTime > b.startTime, {
    message: 'endTime after startTime',
  });

export const publicMenuItemSchema = z.object({
  title: z.string(),
  path: z.string(),
  enabled: z.boolean().optional(),
});
export type PublicMenuItemShape = z.infer<typeof publicMenuItemSchema>;

export const publicConfigSchema = z.object({
  modules: z.record(z.string(), z.boolean()),
  business: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    address: z.string(),
    hours: z.string().optional(),
    timezone: z.string().optional(),
  }),
  site: z
    .object({
      name: z.string(),
      tagline: z.string(),
      blogName: z.string(),
      heroHeadline: z.string(),
    })
    .optional(),
  menu: z.array(publicMenuItemSchema).nullable().optional(),
  wordpress: z.object({ apiUrl: z.string().nullable() }).optional(),
  features: z
    .object({
      darkMode: z.boolean(),
      newsletter: z.boolean(),
      socialSharing: z.boolean(),
      comments: z.boolean(),
      search: z.boolean(),
    })
    .optional(),
  social: z.object({ platforms: z.array(z.string()) }).optional(),
  stripePublishableKey: z.string().nullable(),
});
export type PublicConfig = z.infer<typeof publicConfigSchema>;

// ---- PRP-7: reviews ----
export const reviewSubmissionSchema = z.object({
  rating: z.number().int().min(1, 'Please select a rating').max(5),
  title: z.string().trim().min(2, 'Add a short title').max(120),
  body: z.string().trim().min(10, 'Tell us a bit more (10+ characters)').max(2000),
});
export type ReviewSubmission = z.infer<typeof reviewSubmissionSchema>;

export const reviewInviteContextSchema = z.object({
  serviceName: z.string(),
  serviceDate: z.string(),
  firstName: z.string(),
});
export type ReviewInviteContext = z.infer<typeof reviewInviteContextSchema>;

export const publicReviewSchema = z.object({
  id: z.string().uuid(),
  rating: z.number().int().min(1).max(5),
  title: z.string(),
  body: z.string(),
  reviewerName: z.string(), // "Sarah M." — sanitized server-side, never in the frontend
  submittedAt: z.string(),
});
export type PublicReview = z.infer<typeof publicReviewSchema>;

export const publicReviewsResponseSchema = z.object({
  items: z.array(publicReviewSchema),
  total: z.number().int(),
  page: z.number().int(),
  perPage: z.number().int(),
  averageRating: z.number().nullable(),
});
export type PublicReviewsResponse = z.infer<typeof publicReviewsResponseSchema>;

export const adminReviewStatusSchema = z.object({
  status: z.enum(['pending', 'approved', 'hidden']),
});

// ---- PRP-12: event signups (relay POST /api/events/:id/signup) ----
// `website` is the honeypot: the relay answers a filled one with 200 {ok:true}
// BEFORE validation, so the max(0) rule only guards direct schema use.
export const eventSignupSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name').max(200),
  email: z.string().trim().email('Please enter a valid email'),
  phone: z.string().trim().max(50).optional(),
  spaces: z.number().int().min(1).max(4).default(1),
  website: z.string().max(0).optional(),
});
export type EventSignupInput = z.input<typeof eventSignupSchema>;
export type EventSignup = z.output<typeof eventSignupSchema>;

export const eventSignupResponseSchema = z.union([
  // created and duplicate answer identically (no email-existence oracle)
  z.object({ ok: z.literal(true) }),
  z.object({
    error: z.enum(['full', 'closed', 'not_found', 'signups_unavailable', 'rate_limited', 'validation_error']),
    spotsLeft: z.number().int().optional(),
  }),
]);
export type EventSignupResponse = z.infer<typeof eventSignupResponseSchema>;
